import React, { useState, useCallback, useEffect } from "react";
import SidebarPanel from "./SidebarPanel";

const ResizableSidebar = ({ sidebarProps }) => {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load saved width from localStorage, with a responsive default
    const savedWidth = localStorage.getItem('sidebarWidth');
    if (savedWidth) {
      return parseInt(savedWidth, 10);
    }
    // Default to 25% of screen width, but ensure it's within reasonable bounds
    const quarterWidth = window.innerWidth * 0.25;
    return Math.max(200, Math.min(quarterWidth, window.innerWidth * 0.5));
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const minWidth = 200;
  const maxWidthPercent = 0.5; // 50% of screen width
  const mobileBreakpoint = 768;

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (e) => {
      if (isResizing) {
        const newWidth = e.clientX;
        const maxWidth = window.innerWidth * maxWidthPercent;
        if (newWidth >= minWidth && newWidth <= maxWidth) {
          setSidebarWidth(newWidth);
          // Save to localStorage
          localStorage.setItem('sidebarWidth', newWidth.toString());
        }
      }
    },
    [isResizing, maxWidthPercent, minWidth]
  );

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", resize);
      document.addEventListener("mouseup", stopResizing);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", resize);
        document.removeEventListener("mouseup", stopResizing);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, resize, stopResizing]);

  // Handle window resize to ensure sidebar doesn't exceed viewport
  useEffect(() => {
    const handleWindowResize = () => {
      const windowWidth = window.innerWidth;
      setIsMobile(windowWidth < mobileBreakpoint);

      if (windowWidth >= mobileBreakpoint) {
        const maxAllowedWidth = windowWidth * maxWidthPercent;
        setSidebarWidth((prevWidth) => {
          const newWidth = Math.min(prevWidth, maxAllowedWidth);
          // Save to localStorage if width changed
          if (newWidth !== prevWidth) {
            localStorage.setItem('sidebarWidth', newWidth.toString());
          }
          return newWidth;
        });
      }
    };

    window.addEventListener("resize", handleWindowResize);
    handleWindowResize(); // Check on mount

    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  if (!sidebarProps.sidebarVisible) {
    return null;
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark"
          style={{
            opacity: 0.5,
            zIndex: 1040,
          }}
          onClick={() =>
            sidebarProps.toggleSidebar && sidebarProps.toggleSidebar()
          }
        />
      )}

      <div
        className={`border-end bg-light sidebar-panel shadow-sm slide-in-left ${
          isMobile ? "position-fixed" : "position-relative"
        }`}
        style={{
          width: isMobile ? "100%" : `${sidebarWidth}px`,
          maxWidth: isMobile ? "100%" : `${maxWidthPercent * 100}vw`,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          transition: isResizing ? "none" : "width 0.2s ease",
          zIndex: isMobile ? 1050 : "auto",
          left: isMobile ? 0 : "auto",
          top: isMobile ? 0 : "auto",
        }}
      >
        <div
          style={{ height: "100%", overflow: "hidden", position: "relative" }}
        >
          <SidebarPanel
            {...sidebarProps}
            isMobile={isMobile}
            toggleSidebar={sidebarProps.toggleSidebar}
          />
        </div>

        {/* Resize Handle - Hidden on mobile */}
        {!isMobile && (
          <div
            className="sidebar-resize-handle d-flex align-items-center justify-content-center"
            onMouseDown={startResizing}
            style={{
              position: "absolute",
              right: -3,
              top: 0,
              bottom: 0,
              width: "6px",
              cursor: "col-resize",
              backgroundColor: "rgba(108, 117, 125, 0.2)",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                width: "2px",
                height: "24px",
                backgroundColor: "rgba(108, 117, 125, 0.8)",
                borderRadius: "1px",
                margin: "0 1px",
              }}
            />
            <div
              style={{
                width: "2px",
                height: "24px",
                backgroundColor: "rgba(108, 117, 125, 0.8)",
                borderRadius: "1px",
                margin: "0 1px",
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ResizableSidebar;
